import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Table, Spin, Button, Modal, Tooltip, Grid, Form, message } from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import StockCreationForm from "../../Forms/StockCreationForm/StockCreationForm";
import { updateStock } from "../../../../Services/StockService";
import SkuEditForm from "../../Forms/SkuEditForm/SkuEditForm";

const { useBreakpoint } = Grid;

const StockTable = ({
  history,
  loading,
  data,
  pagination,
  callReFetch,
  dispatch,
  handleDelete,
  isShippingMode,
  userData,
  onPaginationChange,
}) => {
  const screens = useBreakpoint(); // Detect screen size dynamically
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isEditSKUModalVisible, setIsEditSKUModalVisible] = useState(false);
  const [pendingEditModal, setPendingEditModal] = useState(false);
  const [pendingSKUModal, setPendingSKUModal] = useState(false);

  const [selectedStock, setSelectedStock] = useState(null);
  const [form] = Form.useForm();

  // Effect to handle modal opening after selectedStock is set
  useEffect(() => {
    if (selectedStock && pendingEditModal) {
      setIsEditModalVisible(true);
      setPendingEditModal(false);
    }
  }, [selectedStock, pendingEditModal]);

  useEffect(() => {
    if (selectedStock && pendingSKUModal) {
      setIsEditSKUModalVisible(true);
      setPendingSKUModal(false);
    }
  }, [selectedStock, pendingSKUModal]);

  // --- Access control (robust + case-insensitive) ---
  const ADMIN_ALLOWED_USERS = new Set(["leo", "yonathan"]);
  const rawUsername = (userData?.username ?? userData?.name ?? "")
    .toString()
    .trim()
    .toLowerCase();
  const isWhitelistedUser = ADMIN_ALLOWED_USERS.has(rawUsername);

  const rolesRaw = Array.isArray(userData?.roles)
    ? userData.roles
    : userData?.role
    ? [userData.role]
    : userData?.roleName
    ? [userData.roleName]
    : [];
  const roles = rolesRaw
    .filter(Boolean)
    .map((r) => r.toString().trim().toLowerCase());
  const hasAdminRole = roles.includes("admin");

  const isAdmin = isWhitelistedUser && hasAdminRole;

  const handleOpenEditModal = async (id) => {
    const stockToEdit = data.find((stock) => stock._id === id);
    if (stockToEdit) {
      setSelectedStock(stockToEdit);
      setPendingEditModal(true); // Set pending state
    }
  };

  const handleChangeSKU = async (id) => {
    const stockToEdit = data.find((stock) => stock._id === id);
    if (stockToEdit) {
      setSelectedStock(stockToEdit);
      setPendingSKUModal(true); // Set pending state
    }
  };

  const handleCloseEditModal = () => {
    setIsEditModalVisible(false);
    setIsEditSKUModalVisible(false);
    setSelectedStock(null);
    setPendingEditModal(false);
    setPendingSKUModal(false);
    form.resetFields();
  };

  const handleUpdateStock = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        ...values,
        currentQuantity: values.currentQuantity,
        sku: values.sku,
      };

      const response = await updateStock(selectedStock._id, payload);
      if (response) {
        message.success("Stock updated successfully");
        handleCloseEditModal();
        callReFetch();
      }
    } catch (error) {
      console.error("Error updating stock:", error);
      message.error("Failed to update stock");
    }
  };

  // --- Action buttons with full-received check & correct _id usage ---
  const renderActionButton = (item) => {
    const { _id, status, receivedQuantity, currentQuantity } = item;

    if (!isAdmin) return null;

    const rq = receivedQuantity == null ? null : Number(receivedQuantity);
    const cq = currentQuantity == null ? null : Number(currentQuantity);
    if (rq == null || cq == null || rq !== cq) return null;

    if (userData?.counterCode !== "ECOMMRI") {
      return (
        <>
          {status === "in stock" && !roles.includes("spg") && (
            <Tooltip title="Edit">
              <Button
                icon={<EditOutlined />}
                onClick={() => handleOpenEditModal(_id)}
                size={screens.xs ? "small" : "middle"}
                shape="circle"
                style={{ marginRight: 8 }}
              />
            </Tooltip>
          )}
          {/* Uncomment to enable Delete:
          <Tooltip title="Delete">
            <Button
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(_id)}
              size={screens.xs ? "small" : "middle"}
              shape="circle"
              danger
            />
          </Tooltip>
          */}
        </>
      );
    }

    // ECOMMRI: Change SKU Request
    return (
      <Tooltip title="Change SKU Request">
        <Button
          icon={<EditOutlined />}
          onClick={() => handleChangeSKU(_id)}
          size={screens.xs ? "small" : "middle"}
          shape="circle"
        />
      </Tooltip>
    );
  };

  const handleTableOnChange = (paginationParams, tableFilters = {}, sorter) => {
    // Always keep sorter up to date
    dispatch({ type: "sorter", payload: sorter });

    // Only update filters if AntD actually sent any (pagination-only sends empty {})
    const hasFilters =
      tableFilters &&
      Object.values(tableFilters).some((v) =>
        Array.isArray(v) ? v.length > 0 : v != null
      );

    if (hasFilters) {
      // If you also keep non-table filters (like counterCode) in parent state.filteredInfo,
      // this won't nuke them on pagination because we only dispatch when actual filters exist.
      dispatch({ type: "filteredInfo", payload: tableFilters });
    }

    // Pagination change
    if (onPaginationChange && paginationParams) {
      onPaginationChange(paginationParams.current, paginationParams.pageSize);
    }
  };
  

  const allColumns = [
    { title: "No SJ", dataIndex: "noSJ", key: "noSJ", sorter: true },
    {
      title: "CounterCode",
      dataIndex: "counterCode",
      key: "counterCode",
      sorter: true,
    },
    { title: "Article", dataIndex: "article", key: "article", sorter: true },
    {
      title: "Release Date",
      dataIndex: "released_date",
      key: "released_date",
      sorter: true,
      render: (date) => {
        if (!date) return "-";
        const d = new Date(date);
        if (isNaN(d.getTime())) return "-";
        return `${d.getFullYear()}-${(d.getMonth() + 1)
          .toString()
          .padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
      },
    },
    { title: "Color", dataIndex: "color", key: "color", sorter: true },
    { title: "Size", dataIndex: "size", key: "size", sorter: true },
    {
      title: "Start Quantity",
      dataIndex: "quantity",
      key: "quantity",
      sorter: true,
    },
    {
      title: "Received Quantity",
      dataIndex: "receivedQuantity",
      key: "receivedQuantity",
      sorter: true,
    },
    {
      title: "Current Quantity",
      dataIndex: "currentQuantity",
      key: "currentQuantity",
      sorter: true,
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      sorter: true,
      render: (price) =>
        price == null ? "-" : `Rp ${Number(price).toLocaleString("id-ID")}`,
    },
    { title: "Status", dataIndex: "status", key: "status", sorter: true },
    { title: "Barcode", dataIndex: "barcode", key: "barcode", sorter: true },
    {
      title: "Keterangan",
      dataIndex: "keterangan",
      key: "keterangan",
      sorter: true,
    },
    {
      title: "tanggal_kirim",
      dataIndex: "tanggal_kirim",
      key: "tanggal_kirim",
      sorter: true,
    },
    {
      title: "Age (Days)",
      dataIndex: "tanggal_kirim",
      key: "age",
      sorter: (a, b) => new Date(a.tanggal_kirim) - new Date(b.tanggal_kirim),
      render: (tanggal_kirim) => {
        const diffInDays = Math.floor(
          (new Date() - new Date(tanggal_kirim)) / (1000 * 3600 * 24)
        );
        return diffInDays;
      },
    },
    ...(userData?.counterCode === "ECOMMRI"
      ? [
          {
            title: "SKU",
            dataIndex: "sku",
            key: "sku",
            sorter: true,
          },
        ]
      : []),
    {
      title: "Action",
      render: (item) => renderActionButton(item),
      width: screens.xs ? 120 : 200,
    },
  ];

  const filteredColumns = isShippingMode
    ? allColumns.filter((col) =>
        ["article", "color", "size", "quantity", "status"].includes(col.key)
      )
    : allColumns;

  return (
    <div
      style={{
        width: "100%",
        overflowX: "auto",
        padding: screens.xs ? "10px" : "20px",
      }}
    >
      <Spin spinning={loading} tip="Loading stock data...">
        <Table
          columns={filteredColumns}
          dataSource={data}
          onChange={handleTableOnChange}
          pagination={{
            current: pagination?.current || 1,
            pageSize: pagination?.pageSize || (screens.xs ? 5 : 10),
            total: pagination?.total || 0,
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
            pageSizeOptions: ["10", "20", "50", "100"],
          }}
          scroll={screens.xs ? { x: "max-content" } : undefined}
          rowKey="_id"
        />
      </Spin>

      <Modal
        title="Edit Stock"
        open={isEditModalVisible}
        onCancel={handleCloseEditModal}
        onOk={handleUpdateStock}
        okText="Update"
        width={screens.xs ? "90vw" : "50vw"}
      >
        {selectedStock ? (
          <StockCreationForm
            history={history}
            isEditMode
            callReFetch={callReFetch}
            onClose={handleCloseEditModal}
            stockData={selectedStock}
            ArticleData={[]}
            ColorData={[]}
            modalForm={form}
          />
        ) : (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin size="large" tip="Loading stock data..." />
          </div>
        )}
      </Modal>

      <Modal
        title="Edit SKU"
        open={isEditSKUModalVisible}
        onCancel={handleCloseEditModal}
        onOk={handleUpdateStock}
        okText="Update"
        width={screens.xs ? "90vw" : "50vw"}
      >
        {selectedStock ? (
          <SkuEditForm
            history={history}
            isEditMode
            callReFetch={callReFetch}
            onClose={handleCloseEditModal}
            stockData={selectedStock}
            modalForm={form}
          />
        ) : (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin size="large" tip="Loading stock data..." />
          </div>
        )}
      </Modal>
    </div>
  );
};

StockTable.propTypes = {
  userData: PropTypes.object.isRequired,
};

export default StockTable;
